import { useMemo } from 'react';
import { useSubscriptionInfo } from './useBilling';

export interface TrialStatus {
  isTrialing: boolean;
  isExpired: boolean;
  isInGracePeriod: boolean;
  isLocked: boolean;
  daysRemaining: number;
  trialEndsAt: Date | null;
  gracePeriodEndsAt: Date | null;
  status: 'trialing' | 'active' | 'grace_period' | 'locked' | 'past_due' | 'cancelled' | 'unpaid';
}

const GRACE_PERIOD_DAYS = 3;

export function useTrialStatus(): TrialStatus & { isLoading: boolean } {
  const { data: subscription, isLoading } = useSubscriptionInfo();

  return useMemo(() => {
    if (!subscription) {
      return {
        isTrialing: false,
        isExpired: false,
        isInGracePeriod: false,
        isLocked: false,
        daysRemaining: 0,
        trialEndsAt: null,
        gracePeriodEndsAt: null,
        status: 'active' as const,
        isLoading,
      };
    }

    const now = new Date();
    const trialEndsAt = subscription.trial_ends_at ? new Date(subscription.trial_ends_at) : null;
    const isTrialing = subscription.status === 'trialing';
    const isPastDue = subscription.status === 'past_due';
    const isCancelled = subscription.status === 'cancelled';
    const isUnpaid = subscription.status === 'unpaid';

    // Calculate days remaining in trial
    let daysRemaining = 0;
    if (trialEndsAt && isTrialing) {
      daysRemaining = Math.max(0, Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    }

    // Calculate grace period
    const gracePeriodEndsAt = trialEndsAt
      ? new Date(trialEndsAt.getTime() + GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000)
      : null;

    // Determine trial expiry state
    const trialExpired = isTrialing && trialEndsAt && now > trialEndsAt;
    const inGracePeriod = trialExpired && gracePeriodEndsAt && now <= gracePeriodEndsAt;
    const isLocked = (trialExpired && gracePeriodEndsAt && now > gracePeriodEndsAt) || isCancelled || isUnpaid;

    // Determine effective status
    let status: TrialStatus['status'] = 'active';
    if (isTrialing && !trialExpired) {
      status = 'trialing';
    } else if (inGracePeriod) {
      status = 'grace_period';
    } else if (isLocked) {
      status = 'locked';
    } else if (isPastDue) {
      status = 'past_due';
    } else if (isCancelled) {
      status = 'cancelled';
    } else if (isUnpaid) {
      status = 'unpaid';
    }

    return {
      isTrialing: isTrialing && !trialExpired,
      isExpired: !!trialExpired,
      isInGracePeriod: !!inGracePeriod,
      isLocked: !!isLocked,
      daysRemaining,
      trialEndsAt,
      gracePeriodEndsAt,
      status,
      isLoading,
    };
  }, [subscription, isLoading]);
}
